import React from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeCheck, Shield, Zap, Star } from 'lucide-react';

/**
 * TrustBadges - Premium trust signal components
 * - VerifiedSellerBadge: Shows seller is verified
 * - BuyerProtection: Short trust text under price
 * - BoostedBadge: Shows ad is boosted/promoted
 * - SponsoredLabel: Subtle "Sponsored" indicator
 */

// Verified Seller Badge - Premium compact chip style
export function VerifiedSellerBadge({ size = 'sm', className = '' }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  
  const sizes = {
    xs: { icon: 10, text: '10px', padding: '3px 8px', gap: '4px' },
    sm: { icon: 11, text: '11px', padding: '4px 10px', gap: '5px' },
    md: { icon: 13, text: '12px', padding: '5px 12px', gap: '6px' },
  };
  
  const s = sizes[size] || sizes.sm;
  
  return (
    <span 
      className={`verified-seller-badge ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: s.gap,
        padding: s.padding,
        // Premium soft green tint (not loud gradient)
        background: 'rgba(16, 185, 129, 0.12)',
        borderRadius: '999px',
        fontSize: s.text,
        fontWeight: 600,
        color: '#195B2F',
        border: '1px solid rgba(16, 185, 129, 0.22)',
        letterSpacing: '0.1px',
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}
      data-testid="verified-seller-badge"
    >
      <BadgeCheck size={s.icon} style={{ flexShrink: 0 }} />
      <span>{t('trusbadg.verified')}</span>
    </span>
  );
}

// Boosted Ad Badge - Shows ad is promoted
export function BoostedBadge({ size = 'sm', className = '' }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  
  const sizes = {
    xs: { icon: 10, text: '9px', padding: '2px 6px', gap: '3px' },
    sm: { icon: 12, text: '11px', padding: '3px 8px', gap: '4px' },
    md: { icon: 14, text: '12px', padding: '4px 10px', gap: '5px' },
  };
  
  const s = sizes[size] || sizes.sm;
  
  return (
    <span 
      className={`boosted-badge ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: s.gap,
        padding: s.padding,
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        borderRadius: '999px',
        fontSize: s.text,
        fontWeight: 700,
        color: '#ffffff',
        letterSpacing: '0.2px',
        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.25)',
        whiteSpace: 'nowrap',
      }}
      data-testid="boosted-badge"
    >
      <Zap size={s.icon} style={{ flexShrink: 0 }} />
      <span>{t('trusbadg.boosted')}</span>
    </span>
  );
}

// Sponsored Label - Subtle, transparent indicator
export function SponsoredLabel({ className = '' }) {
  const { t } = useTranslation();
  
  return (
    <span 
      className={`sponsored-label ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        padding: '2px 6px',
        background: 'rgba(107, 114, 128, 0.08)',
        borderRadius: '4px',
        fontSize: '9px',
        fontWeight: 600,
        color: '#6b7280',
        letterSpacing: '0.3px',
        textTransform: 'uppercase',
      }}
      data-testid="sponsored-label"
    >
      <Star size={8} style={{ flexShrink: 0 }} />
      <span>{t('adDetails.ad')}</span>
    </span>
  );
}

// Buyer Protection Text - Trust signal under price
export function BuyerProtection({ variant = 'inline', className = '' }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  
  if (variant === 'card') {
    // Compact version for product cards
    return (
      <div 
        className={`buyer-protection-card ${className}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginTop: '4px',
        }}
        data-testid="buyer-protection-card"
      >
        <Shield size={10} style={{ color: '#1F6F3A', flexShrink: 0 }} />
        <span style={{
          fontSize: '9px',
          fontWeight: 500,
          color: '#1F6F3A',
          letterSpacing: '0.1px',
        }}>
          {t('trusbadg.buyerProtection')}
        </span>
      </div>
    );
  }
  
  // Full version for AdDetails page
  return (
    <div 
      className={`buyer-protection ${className}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '12px 14px',
        background: 'linear-gradient(135deg, rgba(31, 111, 58, 0.06) 0%, rgba(16, 185, 129, 0.04) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(31, 111, 58, 0.12)',
        marginTop: '12px',
      }}
      data-testid="buyer-protection"
    >
      <Shield 
        size={20} 
        style={{ 
          color: '#1F6F3A', 
          flexShrink: 0,
          marginTop: '1px',
        }} 
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '13px',
          fontWeight: 700,
          color: '#195B2F',
          marginBottom: '2px',
        }}>
          {t('trusbadg.buyerProtection')}
        </div>
        <div style={{
          fontSize: '11px',
          fontWeight: 500,
          color: '#1F6F3A',
          lineHeight: 1.4,
          opacity: 0.85,
        }}>
          {t('trusbadg.shopSafelyNeverPayUpfront')}
        </div>
      </div>
    </div>
  );
}

// Trust Section - Combined trust signals for AdDetails
export function TrustSection({ seller, className = '' }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const isVerified = seller?.is_verified || seller?.verified;
  
  return (
    <div 
      className={`trust-section ${className}`}
      style={{
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
      }}
      data-testid="trust-section"
    >
      <div style={{
        fontSize: '12px',
        fontWeight: 600,
        color: '#6b7280',
        marginBottom: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}>
        {t('trusbadg.trustSafety')}
      </div>
      
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center',
      }}>
        {isVerified && <VerifiedSellerBadge size="md" />}
        
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          padding: '4px 10px',
          background: 'rgba(107, 114, 128, 0.08)',
          borderRadius: '999px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#4b5563',
        }}>
          <Shield size={14} />
          <span>{t('trusbadg.safeContact')}</span>
        </span>
      </div>
      
      <BuyerProtection variant="inline" />
    </div>
  );
}

// Check if seller is verified
export function isSellerVerified(seller) {
  return seller?.is_verified || seller?.verified || false;
}

export default {
  VerifiedSellerBadge,
  BoostedBadge,
  SponsoredLabel,
  BuyerProtection,
  TrustSection,
  isSellerVerified,
};
